import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { getHeaders } from "@/contexts/AuthContext";

interface ForecastingTabProps {
  propertyId: string;
}

interface ForecastRecord {
  date: string;
  occupancyPct: number;
  revenue: number;
  revPOR: number;
  revPAR: number;
  hkHours: number;
}

interface ForecastResponse {
  propertyId: string;
  horizon: number;
  computedAt: string;
  forecasts: ForecastRecord[];
}

type Horizon = "30" | "60" | "90";

const HORIZONS: { label: string; value: Horizon }[] = [
  { label: "30 Days", value: "30" },
  { label: "60 Days", value: "60" },
  { label: "90 Days", value: "90" },
];

function formatCurrency(val: any): string {
  if (val == null || val === "") return "-";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);
}

function formatPercent(val: any): string {
  if (val == null || val === "") return "-";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return `${(n * (n <= 1 ? 100 : 1)).toFixed(1)}%`;
}

function formatDate(raw: string): string {
  if (!raw) return "-";
  try {
    return new Date(raw).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return raw;
  }
}

const ForecastingTab: React.FC<ForecastingTabProps> = ({ propertyId }) => {
  const [horizon, setHorizon] = useState<Horizon>("30");
  const [forecastData, setForecastData] = useState<ForecastRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchForecast = async (selectedHorizon: Horizon) => {
    if (!propertyId) return;
    setIsLoading(true);
    setForecastData([]);
    try {
      const headers = await getHeaders();
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/forecast/${propertyId}?horizon=${selectedHorizon}`,
        { method: "GET", headers }
      );
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(text || `Request failed: ${resp.status}`);
      }
      const data = await resp.json();
      setForecastData(Array.isArray(data?.forecasts) ? data.forecasts : []);
    } catch (err: any) {
      console.error("Forecast fetch error", err);
      toast({ title: "Error", description: err?.message || "Failed to load forecast data.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchForecast(horizon);
    }
  }, [propertyId]);

  const handleHorizonChange = (value: string) => {
    const h = value as Horizon;
    setHorizon(h);
    fetchForecast(h);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Forecasting</CardTitle>
          <CardDescription>View automated forecast data for this property based on historical values.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Forecast Horizon</Label>
              <Select value={horizon} onValueChange={handleHorizonChange} disabled={isLoading}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HORIZONS.map((h) => (
                    <SelectItem key={h.value} value={h.value}>
                      {h.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => fetchForecast(horizon)} disabled={isLoading || !propertyId}>
              {isLoading ? "Loading..." : "Refresh"}
            </Button>
          </div>

          <div className="mt-6">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading forecast data...</p>
            ) : forecastData.length === 0 ? (
              <p className="text-sm text-muted-foreground">No forecast data available for this horizon.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2 text-left font-medium">Date</th>
                      <th className="px-4 py-2 text-right font-medium">Occupancy</th>
                      <th className="px-4 py-2 text-right font-medium">Revenue</th>
                      <th className="px-4 py-2 text-right font-medium">RevPAR</th>
                      <th className="px-4 py-2 text-right font-medium">RevPOR</th>
                      <th className="px-4 py-2 text-right font-medium">HK Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecastData.map((row, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-2">{formatDate(row.date)}</td>
                        <td className="px-4 py-2 text-right">{formatPercent(row.occupancyPct)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(row.revenue)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(row.revPAR)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(row.revPOR)}</td>
                        <td className="px-4 py-2 text-right">{row.hkHours?.toFixed(1) ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForecastingTab;
